import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { LoadingSpinnerComponent } from 'app/core/components/loading-spinner/loading-spinner.component';
import { NoDataFoundComponent } from 'app/core/components/no-data-found/no-data-found.component';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';
import {
    DataSourceService,
    DataSourceUsage,
    DataSourceUsageBucket,
    DataSourceUsageItem,
} from '../../../dataSources.service';

export interface DataSourceUsageDialogData {
    eid: string;
    name: string;
}

/** One visible usage section (only buckets with total > 0). */
export interface DataSourceUsageSection {
    key: string;
    labelKey: string;
    icon: string;
    total: number;
    items: DataSourceUsageItem[];
    moreCount: number;
}

const USAGE_BUCKETS: {
    key: keyof DataSourceUsage['blockers'];
    labelKey: string;
    icon: string;
}[] = [
    { key: 'agents', labelKey: 'Agents', icon: 'heroicons_outline:cpu-chip' },
    { key: 'tables', labelKey: 'Tables', icon: 'heroicons_outline:table-cells' },
    { key: 'uploads', labelKey: 'Documents', icon: 'heroicons_outline:document-text' },
    { key: 'externalTables', labelKey: 'External Tables', icon: 'heroicons_outline:server-stack' },
    { key: 'formTables', labelKey: 'Form Tables', icon: 'heroicons_outline:clipboard-document-list' },
    { key: 'trackers', labelKey: 'Trackers', icon: 'heroicons_outline:chart-bar' },
    { key: 'procedures', labelKey: 'Procedures', icon: 'heroicons_outline:queue-list' },
    { key: 'mediaFiles', labelKey: 'Media Files', icon: 'heroicons_outline:photo' },
    { key: 'activeBulkOperations', labelKey: 'Active Bulk Operations', icon: 'heroicons_outline:arrow-path' },
];

@Component({
    selector: 'app-data-source-usage-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        TranslocoModule,
        SaiDialogHeaderComponent,
        LoadingSpinnerComponent,
        NoDataFoundComponent,
    ],
    templateUrl: './data-source-usage-dialog.component.html',
    styleUrls: ['./data-source-usage-dialog.component.scss'],
})
export class DataSourceUsageDialogComponent implements OnInit {
    readonly data = inject<DataSourceUsageDialogData>(MAT_DIALOG_DATA);
    private readonly _dialogRef = inject(MatDialogRef<DataSourceUsageDialogComponent>);
    private readonly _dataSourceService = inject(DataSourceService);
    private readonly _alert = inject(CustomAlertService);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);

    loading = true;
    usage: DataSourceUsage | null = null;
    sections: DataSourceUsageSection[] = [];

    ngOnInit(): void {
        this._dataSourceService
            .getUsage(this.data.eid)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (usage) => {
                    this.usage = usage;
                    this.sections = this._buildSections(usage);
                    this.loading = false;
                    this._cdr.markForCheck();
                },
                error: (err: unknown) => {
                    const message =
                        (err as { error?: { message?: string } })?.error?.message
                        || 'Failed to load data source usage.';
                    this._alert.failedMessage(message);
                    this.loading = false;
                    this._cdr.markForCheck();
                    this._dialogRef.close();
                },
            });
    }

    close(): void {
        this._dialogRef.close();
    }

    private _buildSections(usage: DataSourceUsage): DataSourceUsageSection[] {
        const blockers = usage?.blockers;
        if (!blockers) {
            return [];
        }

        const sections: DataSourceUsageSection[] = [];
        for (const { key, labelKey, icon } of USAGE_BUCKETS) {
            const bucket: DataSourceUsageBucket | undefined = blockers[key];
            const total = bucket?.total ?? 0;
            if (total <= 0) {
                continue;
            }
            const items = Array.isArray(bucket?.items) ? bucket.items : [];
            sections.push({
                key,
                labelKey,
                icon,
                total,
                items,
                moreCount: Math.max(0, total - items.length),
            });
        }
        return sections;
    }
}
